import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Package, Building2, Plus, Edit, Trash2, Clock,
  Users, Shield, FileText, Calendar, BarChart3, MessageSquare, Settings,
  CreditCard, TrendingUp, DollarSign, Activity, Eye, EyeOff,
  PauseCircle, PlayCircle, XCircle, RefreshCw,
  CheckCircle, AlertTriangle, Mail, Key, Save, TestTube, Globe,
  Upload, Image, Download, FileDown, Bot
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MODULE_ICONS = {
  employees: Users,
  habilitations: Shield,
  conges: Calendar,
  payroll: FileText,
  documents: FileText,
  entretiens: MessageSquare,
  expenses: FileText,
  rse: BarChart3,
  ai_chat: MessageSquare,
  calendar: Calendar,
  admin: Settings,
  dashboard: BarChart3
};

const SuperAdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Data states
  const [packages, setPackages] = useState([]);
  const [structures, setStructures] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [availableModules, setAvailableModules] = useState({});
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [showExtendTrialDialog, setShowExtendTrialDialog] = useState(false);
  const [showAssignPackageDialog, setShowAssignPackageDialog] = useState(false);
  const [showStructureDetailDialog, setShowStructureDetailDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  
  // Form states - Package
  const [editingPackageCode, setEditingPackageCode] = useState(null);
  const [formCode, setFormCode] = useState('');
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPriceMonthly, setFormPriceMonthly] = useState(40.83);
  const [formPriceAnnual, setFormPriceAnnual] = useState(441.00);
  const [formModules, setFormModules] = useState([]);
  const [formMaxEmployees, setFormMaxEmployees] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  
  // Form states - Subscription
  const [editingSubscriptionId, setEditingSubscriptionId] = useState(null);
  const [subFormStructureId, setSubFormStructureId] = useState('');
  const [subFormPackageCode, setSubFormPackageCode] = useState('');
  const [subFormPlanType, setSubFormPlanType] = useState('monthly');
  const [subFormStatus, setSubFormStatus] = useState('active');
  const [subFormAmountHt, setSubFormAmountHt] = useState('');
  const [subFormPeriodEnd, setSubFormPeriodEnd] = useState('');
  const [subFormIsFree, setSubFormIsFree] = useState(false);
  const [subFormFreeReason, setSubFormFreeReason] = useState('');
  const [subFormFreeUntil, setSubFormFreeUntil] = useState('');
  const [subFormNotifyClient, setSubFormNotifyClient] = useState(true);

  // Form states - Settings
  const [savingSettings, setSavingSettings] = useState(false);
  const [stripeApiKey, setStripeApiKey] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [anthropicApiKey, setAnthropicApiKey] = useState('');
  const [aiModel, setAiModel] = useState('claude-sonnet-4-5-20250929');
  const [smtpFromEmail, setSmtpFromEmail] = useState('');
  const [smtpFromName, setSmtpFromName] = useState('');
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [tvaRate, setTvaRate] = useState('20');
  const [trialDays, setTrialDays] = useState('14');
  const [testingEmail, setTestingEmail] = useState(false);

  // Form states - Company Info (Société qui facture)
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyCodePostal, setCompanyCodePostal] = useState('');
  const [companyVille, setCompanyVille] = useState('');
  const [companySiret, setCompanySiret] = useState('');
  const [companyTva, setCompanyTva] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyIban, setCompanyIban] = useState('');
  const [companyBic, setCompanyBic] = useState('');
  const [companyCapital, setCompanyCapital] = useState('');
  const [companyRcs, setCompanyRcs] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [savingCompany, setSavingCompany] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Invoice export states
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportingInvoices, setExportingInvoices] = useState(false);
  const [invoiceExportResult, setInvoiceExportResult] = useState(null);
  
  // Selected items
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [extendDays, setExtendDays] = useState(7);
  const [trialMode, setTrialMode] = useState('extend'); // 'extend' or 'set'
  const [trialEndDate, setTrialEndDate] = useState('');
  const [assignPackageCode, setAssignPackageCode] = useState('');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}` };
  }, []);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/');
      return;
    }
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [packagesRes, structuresRes, modulesRes, subscriptionsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/superadmin/packages`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/superadmin/structures`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/modules/available`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/superadmin/subscriptions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/superadmin/settings`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setPackages(packagesRes.data || []);
      setStructures(structuresRes.data || []);
      setAvailableModules(modulesRes.data || {});
      setSubscriptions(subscriptionsRes.data || []);
      
      // Load settings into form
      if (settingsRes.data) {
        setSettings(settingsRes.data);
        setSmtpHost(settingsRes.data.smtp_host || '');
        setSmtpPort(String(settingsRes.data.smtp_port || 587));
        setSmtpUsername(settingsRes.data.smtp_username || '');
        setSmtpFromEmail(settingsRes.data.smtp_from_email || '');
        setSmtpFromName(settingsRes.data.smtp_from_name || '');
        setSmtpUseTls(settingsRes.data.smtp_use_tls !== false);
        setAiModel(settingsRes.data.ai_model || 'claude-sonnet-4-5-20250929');
        setTvaRate(String(settingsRes.data.tva_rate || 20));
        setTrialDays(String(settingsRes.data.trial_days || 14));
        
        // Load company info
        setCompanyName(settingsRes.data.company_name || '');
        setCompanyAddress(settingsRes.data.company_address || '');
        setCompanyCodePostal(settingsRes.data.company_code_postal || '');
        setCompanyVille(settingsRes.data.company_ville || '');
        setCompanySiret(settingsRes.data.company_siret || '');
        setCompanyTva(settingsRes.data.company_tva || '');
        setCompanyEmail(settingsRes.data.company_email || '');
        setCompanyPhone(settingsRes.data.company_phone || '');
        setCompanyIban(settingsRes.data.company_iban || '');
        setCompanyBic(settingsRes.data.company_bic || '');
        setCompanyCapital(settingsRes.data.company_capital || '');
        setCompanyRcs(settingsRes.data.company_rcs || '');
        setCompanyLogo(settingsRes.data.company_logo_base64 || '');
      }

      // Calculate stats
      calculateStats(structuresRes.data || [], subscriptionsRes.data || [], packagesRes.data || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (structuresData, subscriptionsData, packagesData) => {
    const activeStructures = structuresData.filter(s => s.status === 'active').length;
    const trialStructures = structuresData.filter(s => s.status === 'trial').length;
    const activeSubscriptions = subscriptionsData.filter(s => s.status === 'active').length;
    
    // Calculate MRR (Monthly Recurring Revenue)
    let mrr = 0;
    subscriptionsData.forEach(sub => {
      if (sub.status === 'active') {
        if (sub.plan_type === 'monthly') {
          mrr += sub.amount_ttc || sub.amount_ht * 1.2 || 49;
        } else if (sub.plan_type === 'annual') {
          mrr += (sub.amount_ttc || sub.amount_ht * 1.2 || 529.20) / 12;
        }
      }
    });

    // Calculate ARR
    const arr = mrr * 12;

    setStats({
      totalStructures: structuresData.length,
      activeStructures,
      trialStructures,
      suspendedStructures: structuresData.filter(s => s.status === 'suspended').length,
      totalSubscriptions: subscriptionsData.length,
      activeSubscriptions,
      totalPackages: packagesData.length,
      activePackages: packagesData.filter(p => p.is_active).length,
      mrr: mrr.toFixed(2),
      arr: arr.toFixed(2),
      conversionRate: (activeStructures + trialStructures) > 0 ? ((activeStructures / (activeStructures + trialStructures)) * 100).toFixed(1) : 0
    });
  };

  // ==================== PACKAGE FUNCTIONS ====================
  const resetPackageForm = () => {
    setFormCode('');
    setFormName('');
    setFormDescription('');
    setFormPriceMonthly(40.83);
    setFormPriceAnnual(441.00);
    setFormModules([]);
    setFormMaxEmployees('');
    setFormIsActive(true);
    setEditingPackageCode(null);
  };

  const openCreatePackage = () => {
    resetPackageForm();
    setShowPackageDialog(true);
  };

  const openEditPackage = (pkg) => {
    setEditingPackageCode(pkg.code);
    setFormCode(pkg.code);
    setFormName(pkg.name);
    setFormDescription(pkg.description || '');
    setFormPriceMonthly(pkg.price_monthly_ht);
    setFormPriceAnnual(pkg.price_annual_ht);
    setFormModules([...(pkg.modules || [])]);
    setFormMaxEmployees(pkg.max_employees ? String(pkg.max_employees) : '');
    setFormIsActive(pkg.is_active);
    setShowPackageDialog(true);
  };

  const handleSavePackage = async () => {
    if (!formCode || !formName) {
      toast.error('Code et nom sont requis');
      return;
    }

    const isEdit = editingPackageCode !== null;
    const url = isEdit 
      ? `${API_URL}/superadmin/packages/${editingPackageCode}`
      : `${API_URL}/superadmin/packages`;
    
    const body = {
      code: formCode,
      name: formName,
      description: formDescription,
      price_monthly_ht: parseFloat(formPriceMonthly) || 0,
      price_annual_ht: parseFloat(formPriceAnnual) || 0,
      modules: formModules,
      max_employees: formMaxEmployees ? parseInt(formMaxEmployees) : null,
      is_active: formIsActive
    };

    try {
      const headers = getAuthHeaders();
      if (isEdit) {
        await axios.put(url, body, { headers });
        toast.success('Package mis à jour avec succès');
      } else {
        await axios.post(url, body, { headers });
        toast.success('Package créé avec succès');
      }
      setShowPackageDialog(false);
      resetPackageForm();
      fetchAllData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(message);
    }
  };

  const handleDeletePackage = async (code) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le package "${code}" ?`)) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/superadmin/packages/${code}`, { headers });
      toast.success('Package supprimé avec succès');
      fetchAllData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
    }
  };

  // ==================== SUBSCRIPTION FUNCTIONS ====================
  const resetSubscriptionForm = () => {
    setSubFormStructureId('');
    setSubFormPackageCode('');
    setSubFormPlanType('monthly');
    setSubFormStatus('active');
    setSubFormAmountHt('');
    setSubFormPeriodEnd('');
    setSubFormIsFree(false);
    setSubFormFreeReason('');
    setSubFormFreeUntil('');
    setSubFormNotifyClient(true);
    setEditingSubscriptionId(null);
  };

  const openEditSubscription = (sub) => {
    setEditingSubscriptionId(sub.id);
    setSubFormStructureId(sub.structure_id);
    setSubFormPackageCode(sub.package_code || '');
    setSubFormPlanType(sub.plan_type);
    setSubFormStatus(sub.status);
    setSubFormAmountHt(sub.amount_ht ? String(sub.amount_ht) : '');
    // Period end date (format for input)
    if (sub.current_period_end) {
      const periodEnd = new Date(sub.current_period_end);
      setSubFormPeriodEnd(periodEnd.toISOString().split('T')[0]);
    } else {
      setSubFormPeriodEnd('');
    }
    // Gratuity fields
    setSubFormIsFree(sub.is_free || false);
    setSubFormFreeReason(sub.free_reason || '');
    if (sub.free_until) {
      const freeUntil = new Date(sub.free_until);
      setSubFormFreeUntil(freeUntil.toISOString().split('T')[0]);
    } else {
      setSubFormFreeUntil('');
    }
    setSubFormNotifyClient(true);
    setShowSubscriptionDialog(true);
  };

  const handleSaveSubscription = async () => {
    // Only editing is allowed - subscriptions are created via Stripe
    if (!editingSubscriptionId) {
      toast.error('Les abonnements sont créés via Stripe uniquement');
      return;
    }

    const url = `${API_URL}/superadmin/subscriptions/${editingSubscriptionId}`;

    const body = {
      package_code: subFormPackageCode || null,
      plan_type: subFormPlanType,
      status: subFormStatus,
      amount_ht: subFormAmountHt ? parseFloat(subFormAmountHt) : null,
      current_period_end: subFormPeriodEnd ? new Date(subFormPeriodEnd).toISOString() : null,
      is_free: subFormIsFree,
      free_reason: subFormIsFree ? subFormFreeReason : null,
      free_until: subFormIsFree && subFormFreeUntil ? new Date(subFormFreeUntil).toISOString() : null,
      notify_client: subFormNotifyClient
    };

    try {
      const headers = getAuthHeaders();
      const response = await axios.put(url, body, { headers });
      const message = response.data?.message || 'Abonnement mis à jour';
      toast.success(message);
      setShowSubscriptionDialog(false);
      resetSubscriptionForm();
      fetchAllData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(message);
    }
  };

  const handleDeleteSubscription = async (id) => {
    if (!window.confirm('Supprimer cet abonnement ?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/superadmin/subscriptions/${id}`, { headers });
      toast.success('Abonnement supprimé');
      fetchAllData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
    }
  };

  // ==================== STRUCTURE FUNCTIONS ====================
  const openStructureDetail = (structure) => {
    setSelectedStructure(structure);
    setShowStructureDetailDialog(true);
  };

  const openExtendTrial = (structure) => {
    setSelectedStructure(structure);
    setExtendDays(7);
    setShowExtendTrialDialog(true);
  };

  const handleExtendTrial = async () => {
    if (!selectedStructure) return;

    try {
      const headers = getAuthHeaders();
      let response;
      if (trialMode === 'set') {
        response = await axios.post(`${API_URL}/superadmin/set-trial`, {
          structure_id: selectedStructure.id,
          trial_end_date: trialEndDate
        }, { headers });
      } else {
        response = await axios.post(`${API_URL}/superadmin/extend-trial`, {
          structure_id: selectedStructure.id,
          additional_days: extendDays
        }, { headers });
      }

      toast.success(response.data.message || 'Période d\'essai modifiée');
      setShowExtendTrialDialog(false);
      setSelectedStructure(null);
      setTrialMode('extend');
      fetchAllData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur';
      toast.error(message);
    }
  };

  const openAssignPackage = (structure) => {
    setSelectedStructure(structure);
    setAssignPackageCode('');
    setShowAssignPackageDialog(true);
  };

  const handleAssignPackage = async () => {
    if (!selectedStructure || !assignPackageCode) return;
    
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_URL}/superadmin/assign-package`, {
        structure_id: selectedStructure.id,
        package_code: assignPackageCode
      }, { headers });
      
      toast.success(response.data.message || 'Package assigné');
      setShowAssignPackageDialog(false);
      setSelectedStructure(null);
      setAssignPackageCode('');
      fetchAllData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur';
      toast.error(message);
    }
  };

  const handleSuspendStructure = async (structureId) => {
    if (!window.confirm('Suspendre cette structure ?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/superadmin/structures/${structureId}`, {
        status: 'suspended'
      }, { headers });
      toast.success('Structure suspendue');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suspension');
    }
  };

  const handleActivateStructure = async (structureId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/superadmin/structures/${structureId}`, {
        status: 'active'
      }, { headers });
      toast.success('Structure activée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de l\'activation');
    }
  };

  // ==================== SETTINGS FUNCTIONS ====================
  const handleSaveStripeSettings = async () => {
    if (!stripeApiKey) {
      toast.error('Veuillez entrer une clé API Stripe');
      return;
    }
    
    setSavingSettings(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/superadmin/settings`, {
        stripe_api_key: stripeApiKey
      }, { headers });
      toast.success('Clé API Stripe enregistrée');
      setStripeApiKey(''); // Clear for security
      // Reload settings to update the status
      const settingsRes = await axios.get(`${API_URL}/superadmin/settings`, { headers });
      if (settingsRes.data) {
        setSettings(settingsRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveSmtpSettings = async () => {
    setSavingSettings(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/superadmin/settings`, {
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort) || 587,
        smtp_username: smtpUsername,
        smtp_password: smtpPassword || undefined,
        smtp_from_email: smtpFromEmail,
        smtp_from_name: smtpFromName,
        smtp_use_tls: smtpUseTls
      }, { headers });
      toast.success('Configuration SMTP enregistrée');
      setSmtpPassword(''); // Clear password field
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveAnthropicSettings = async () => {
    setSavingSettings(true);
    try {
      const headers = getAuthHeaders();
      const payload = { ai_model: aiModel };
      if (anthropicApiKey) {
        payload.anthropic_api_key = anthropicApiKey;
      }
      await axios.put(`${API_URL}/superadmin/settings`, payload, { headers });
      toast.success('Configuration IA enregistrée');
      setAnthropicApiKey('');
      const settingsRes = await axios.get(`${API_URL}/superadmin/settings`, { headers });
      if (settingsRes.data) {
        setSettings(settingsRes.data);
        setAiModel(settingsRes.data.ai_model || 'claude-sonnet-4-5-20250929');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveGeneralSettings = async () => {
    setSavingSettings(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/superadmin/settings`, {
        tva_rate: parseFloat(tvaRate) || 20,
        trial_days: parseInt(trialDays) || 14
      }, { headers });
      toast.success('Paramètres généraux enregistrés');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveCompanyInfo = async () => {
    setSavingCompany(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/superadmin/settings`, {
        company_name: companyName,
        company_address: companyAddress,
        company_code_postal: companyCodePostal,
        company_ville: companyVille,
        company_siret: companySiret,
        company_tva: companyTva,
        company_email: companyEmail,
        company_phone: companyPhone,
        company_iban: companyIban,
        company_bic: companyBic,
        company_capital: companyCapital,
        company_rcs: companyRcs
      }, { headers });
      toast.success('Informations de la société enregistrées');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorise. Utilisez PNG, JPG ou WEBP.');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Maximum 2MB.');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      };

      await axios.post(`${API_URL}/superadmin/settings/upload-logo`, formData, { headers });
      toast.success('Logo uploadé avec succès');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleExportInvoices = async () => {
    if (!exportStartDate || !exportEndDate) {
      toast.error('Veuillez selectionner une periode');
      return;
    }

    setExportingInvoices(true);
    setInvoiceExportResult(null);

    try {
      const headers = getAuthHeaders();
      const response = await axios.get(
        `${API_URL}/superadmin/invoices/export?start_date=${exportStartDate}&end_date=${exportEndDate}`,
        { headers }
      );

      setInvoiceExportResult(response.data);

      if (response.data.invoices.length === 0) {
        toast.info('Aucune facture trouvee pour cette periode');
      } else {
        toast.success(`${response.data.invoices.length} facture(s) trouvee(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'export');
    } finally {
      setExportingInvoices(false);
    }
  };

  const downloadInvoicePdf = (invoice) => {
    if (!invoice.pdf_base64) {
      toast.error('PDF non disponible');
      return;
    }

    // Create blob and download
    const byteCharacters = atob(invoice.pdf_base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `facture_${invoice.invoice_number}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllInvoices = async () => {
    if (!invoiceExportResult?.invoices?.length) return;

    toast.info('Telechargement en cours...');

    // Download each PDF individually
    for (const invoice of invoiceExportResult.invoices) {
      if (invoice.pdf_base64) {
        downloadInvoicePdf(invoice);
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    toast.success('Telechargement termine');
  };

  const handleTestEmail = async () => {
    if (!settings?.smtp_configured) {
      toast.error('Configurez d\'abord les paramètres SMTP');
      return;
    }
    
    setTestingEmail(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/superadmin/settings/test-email`, {}, { headers });
      toast.success('Email de test envoyé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setTestingEmail(false);
    }
  };

  const toggleModule = (moduleCode) => {
    if (formModules.includes(moduleCode)) {
      setFormModules(formModules.filter(m => m !== moduleCode));
    } else {
      setFormModules([...formModules, moduleCode]);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: 'Actif', cls: 'bg-green-100 text-green-800', icon: CheckCircle },
      trial: { label: 'Essai', cls: 'bg-blue-100 text-blue-800', icon: Clock },
      cancelled: { label: 'Annulé', cls: 'bg-red-100 text-red-800', icon: XCircle },
      suspended: { label: 'Suspendu', cls: 'bg-orange-100 text-orange-800', icon: PauseCircle },
      past_due: { label: 'Impayé', cls: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle }
    };
    const c = config[status] || { label: status, cls: 'bg-slate-100', icon: Activity };
    const Icon = c.icon;
    return (
      <Badge className={`${c.cls} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  // Get structure for a subscription (for report view)
  const getStructureSubscription = (structureId) => {
    return subscriptions.find(s => s.structure_id === structureId);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F5156] mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  const moduleEntries = Object.entries(availableModules);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="superadmin-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Administration Plateforme</h1>
        <p className="text-slate-600 dark:text-slate-400">Tableau de bord SuperAdmin - Nexus SIRH</p>
      </div>

      <Tabs defaultValue="dashboard" className="space-y-6">
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="dashboard" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="packages" className="gap-2">
            <Package className="w-4 h-4" />
            Packages
          </TabsTrigger>
          <TabsTrigger value="structures" className="gap-2">
            <Building2 className="w-4 h-4" />
            Structures
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="gap-2">
            <CreditCard className="w-4 h-4" />
            Abonnements
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            Rapports
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="w-4 h-4" />
            Configuration
          </TabsTrigger>
        </TabsList>

        {/* ==================== DASHBOARD TAB ==================== */}
        <TabsContent value="dashboard">
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Structures totales</p>
                      <p className="text-3xl font-bold">{stats?.totalStructures || 0}</p>
                    </div>
                    <Building2 className="w-12 h-12 text-blue-200" />
                  </div>
                  <div className="mt-4 flex gap-2 text-sm">
                    <span className="bg-white/20 px-2 py-1 rounded">{stats?.activeStructures || 0} actives</span>
                    <span className="bg-white/20 px-2 py-1 rounded">{stats?.trialStructures || 0} en essai</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">MRR (Revenus mensuels)</p>
                      <p className="text-3xl font-bold">{formatCurrency(stats?.mrr || 0)}</p>
                    </div>
                    <DollarSign className="w-12 h-12 text-green-200" />
                  </div>
                  <div className="mt-4 text-sm text-green-100">
                    ARR: {formatCurrency(stats?.arr || 0)}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Abonnements actifs</p>
                      <p className="text-3xl font-bold">{stats?.activeSubscriptions || 0}</p>
                    </div>
                    <CreditCard className="w-12 h-12 text-purple-200" />
                  </div>
                  <div className="mt-4 text-sm text-purple-100">
                    Total: {stats?.totalSubscriptions || 0} abonnements
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">Taux de conversion</p>
                      <p className="text-3xl font-bold">{stats?.conversionRate || 0}%</p>
                    </div>
                    <TrendingUp className="w-12 h-12 text-orange-200" />
                  </div>
                  <div className="mt-4 text-sm text-orange-100">
                    Essai → Payant
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Structures */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Structures récentes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {structures.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Aucune structure</p>
                  ) : (
                    <div className="space-y-3">
                      {structures.slice(0, 5).map((structure) => (
                        <div key={structure.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{structure.nom}</p>
                            <p className="text-sm text-slate-500">{structure.email}</p>
                          </div>
                          {getStatusBadge(structure.status)}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Package Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Packages disponibles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {packages.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-slate-500 mb-2">Aucun package créé</p>
                      <Button onClick={openCreatePackage} size="sm" variant="outline">
                        <Plus className="w-4 h-4 mr-2" />
                        Créer un package
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {packages.map((pkg) => (
                        <div key={pkg.code} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{pkg.name}</p>
                            <p className="text-sm text-slate-500">{pkg.modules?.length || 0} modules</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-[#0F5156]">{formatCurrency(pkg.price_monthly_ttc)}/mois</p>
                            {pkg.is_active ? (
                              <Badge className="bg-green-100 text-green-800">Actif</Badge>
                            ) : (
                              <Badge variant="outline">Inactif</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ==================== PACKAGES TAB ==================== */}
        <TabsContent value="packages">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Packages d'abonnement</CardTitle>
                <CardDescription>Créez et gérez les offres d'abonnement avec accès aux modules</CardDescription>
              </div>
              <Button onClick={openCreatePackage} className="bg-[#0F5156] hover:bg-[#0d464a]">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau package
              </Button>
            </CardHeader>
            <CardContent>
              {packages.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">Aucun package créé</p>
                  <Button onClick={openCreatePackage} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer votre premier package
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Package</TableHead>
                      <TableHead>Prix mensuel</TableHead>
                      <TableHead>Prix annuel</TableHead>
                      <TableHead>Modules</TableHead>
                      <TableHead>Limite</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {packages.map((pkg) => (
                      <TableRow key={pkg.code}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{pkg.name}</p>
                            <p className="text-sm text-slate-500">{pkg.code}</p>
                          </div>
                        </TableCell>
                        <TableCell>{formatCurrency(pkg.price_monthly_ttc)} TTC</TableCell>
                        <TableCell>{formatCurrency(pkg.price_annual_ttc)} TTC</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(pkg.modules || []).slice(0, 3).map((m) => (
                              <Badge key={m} variant="outline" className="text-xs">{m}</Badge>
                            ))}
                            {(pkg.modules || []).length > 3 && (
                              <Badge variant="outline" className="text-xs">+{pkg.modules.length - 3}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{pkg.max_employees || '∞'}</TableCell>
                        <TableCell>
                          {pkg.is_active ? (
                            <Badge className="bg-green-100 text-green-800">Actif</Badge>
                          ) : (
                            <Badge variant="outline">Inactif</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openEditPackage(pkg)}
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeletePackage(pkg.code)}
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== STRUCTURES TAB ==================== */}
        <TabsContent value="structures">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Structures clientes</CardTitle>
                <CardDescription>Gérez les structures, périodes d'essai et packages assignés</CardDescription>
              </div>
              <Button variant="outline" onClick={fetchAllData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </CardHeader>
            <CardContent>
              {structures.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucune structure inscrite</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Structure</TableHead>
                      <TableHead>SIRET</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Fin d'essai</TableHead>
                      <TableHead>Package</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {structures.map((structure) => (
                      <TableRow key={structure.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{structure.nom}</p>
                            <p className="text-sm text-slate-500">{structure.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{structure.siret}</TableCell>
                        <TableCell>{getStatusBadge(structure.status)}</TableCell>
                        <TableCell>
                          {structure.trial_end_date ? (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4 text-slate-400" />
                              {formatDate(structure.trial_end_date)}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {structure.package_code ? (
                            <Badge variant="outline">{structure.package_code}</Badge>
                          ) : (
                            <span className="text-slate-400">Standard</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openStructureDetail(structure)}
                              title="Détails"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openExtendTrial(structure)}
                              title="Prolonger essai"
                            >
                              <Clock className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openAssignPackage(structure)}
                              title="Assigner package"
                            >
                              <Package className="w-4 h-4" />
                            </Button>
                            {structure.status === 'suspended' ? (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleActivateStructure(structure.id)}
                                title="Activer"
                              >
                                <PlayCircle className="w-4 h-4 text-green-500" />
                              </Button>
                            ) : (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleSuspendStructure(structure.id)}
                                title="Suspendre"
                              >
                                <PauseCircle className="w-4 h-4 text-orange-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== SUBSCRIPTIONS TAB ==================== */}
        <TabsContent value="subscriptions">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Abonnements Stripe</CardTitle>
                <CardDescription>Suivi des abonnements payés via Stripe par les structures</CardDescription>
              </div>
              <Button variant="outline" onClick={fetchAllData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </CardHeader>
            <CardContent>
              {subscriptions.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-2">Aucun abonnement actif</p>
                  <p className="text-sm text-slate-400">Les abonnements apparaîtront ici lorsque les structures souscriront via Stripe</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Structure</TableHead>
                      <TableHead>Package</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Montant TTC</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{sub.structure_name || 'N/A'}</p>
                            <p className="text-xs text-slate-400 font-mono">{sub.structure_id?.slice(0, 8)}...</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {sub.package_code ? (
                            <Badge variant="outline">{sub.package_name || sub.package_code}</Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {sub.plan_type === 'monthly' ? 'Mensuel' : 'Annuel'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold">
                          {sub.is_free ? (
                            <Badge className="bg-green-100 text-green-800">GRATUIT</Badge>
                          ) : (
                            formatCurrency(sub.amount_ttc || sub.amount_ht * 1.2)
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {getStatusBadge(sub.status)}
                            {sub.is_free && (
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                Offert {sub.free_until ? `jusqu'au ${formatDate(sub.free_until)}` : ''}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>Du: {formatDate(sub.current_period_start)}</p>
                            <p>Au: {formatDate(sub.current_period_end)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {sub.stripe_subscription_id ? (
                            <Badge className="bg-purple-100 text-purple-800">Stripe</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800">Manuel</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditSubscription(sub)}
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteSubscription(sub.id)}
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== REPORTS TAB ==================== */}
        <TabsContent value="reports">
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    Revenus
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">MRR</span>
                      <span className="font-bold">{formatCurrency(stats?.mrr || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">ARR</span>
                      <span className="font-bold">{formatCurrency(stats?.arr || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Packages</span>
                      <span className="font-bold">{stats?.activePackages || 0} / {stats?.totalPackages || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    Structures
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total</span>
                      <span className="font-bold">{stats?.totalStructures || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Actives</span>
                      <span className="font-bold text-green-600">{stats?.activeStructures || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">En essai</span>
                      <span className="font-bold text-blue-600">{stats?.trialStructures || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Suspendues</span>
                      <span className="font-bold text-orange-600">{stats?.suspendedStructures || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-500" />
                    Conversion
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Taux de conversion</span>
                      <span className="font-bold">{stats?.conversionRate || 0}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Abonnements actifs</span>
                      <span className="font-bold">{stats?.activeSubscriptions || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total abonnements</span>
                      <span className="font-bold">{stats?.totalSubscriptions || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Report */}
            <Card>
              <CardHeader>
                <CardTitle>Rapport détaillé par structure</CardTitle>
                <CardDescription>Vue d'ensemble de toutes les structures et leur état</CardDescription>
              </CardHeader>
              <CardContent>
                {structures.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Aucune donnée disponible</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Structure</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Package</TableHead>
                        <TableHead>Abonnement</TableHead>
                        <TableHead>Date inscription</TableHead>
                        <TableHead>Fin d'essai</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {structures.map((structure) => {
                        const structureSub = subscriptions.find(s => s.structure_id === structure.id);
                        return (
                          <TableRow key={structure.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{structure.nom}</p>
                                <p className="text-xs text-slate-500">{structure.ville}</p>
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(structure.status)}</TableCell>
                            <TableCell>
                              {structure.package_code ? (
                                <Badge>{structure.package_code}</Badge>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {structureSub ? (
                                <div>
                                  <Badge className={structureSub.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100'}>
                                    {structureSub.plan_type === 'monthly' ? 'Mensuel' : 'Annuel'}
                                  </Badge>
                                  <p className="text-xs text-slate-500 mt-1">{formatCurrency(structureSub.amount_ttc)}</p>
                                </div>
                              ) : (
                                <span className="text-slate-400">Aucun</span>
                              )}
                            </TableCell>
                            <TableCell>{formatDate(structure.created_at)}</TableCell>
                            <TableCell>{formatDate(structure.trial_end_date)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Invoice Export Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileDown className="w-5 h-5 text-[#0F5156]" />
                  Export des factures pour comptabilite
                </CardTitle>
                <CardDescription>
                  Exportez toutes les factures d'une periode donnee pour votre comptabilite interne
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date de debut</Label>
                      <Input
                        type="date"
                        value={exportStartDate}
                        onChange={(e) => setExportStartDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Date de fin</Label>
                      <Input
                        type="date"
                        value={exportEndDate}
                        onChange={(e) => setExportEndDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleExportInvoices}
                    disabled={exportingInvoices || !exportStartDate || !exportEndDate}
                    className="bg-[#0F5156] hover:bg-[#0d464a]"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exportingInvoices ? 'Chargement...' : 'Rechercher les factures'}
                  </Button>

                  {/* Export Results */}
                  {invoiceExportResult && (
                    <div className="border rounded-lg p-4 mt-4 space-y-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium">Resultats de l'export</h4>
                          <p className="text-sm text-slate-500">
                            Periode: {invoiceExportResult.period.start} au {invoiceExportResult.period.end}
                          </p>
                        </div>
                        {invoiceExportResult.invoices.length > 0 && (
                          <Button onClick={downloadAllInvoices} variant="outline">
                            <FileDown className="w-4 h-4 mr-2" />
                            Telecharger tous les PDF
                          </Button>
                        )}
                      </div>

                      {/* Summary */}
                      <div className="grid grid-cols-4 gap-4 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[#0F5156]">{invoiceExportResult.summary.count}</p>
                          <p className="text-xs text-slate-500">Factures</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold">{formatCurrency(invoiceExportResult.summary.total_ht)}</p>
                          <p className="text-xs text-slate-500">Total HT</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold">{formatCurrency(invoiceExportResult.summary.total_tva)}</p>
                          <p className="text-xs text-slate-500">TVA</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">{formatCurrency(invoiceExportResult.summary.total_ttc)}</p>
                          <p className="text-xs text-slate-500">Total TTC</p>
                        </div>
                      </div>

                      {/* Invoice List */}
                      {invoiceExportResult.invoices.length > 0 && (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>N° Facture</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Client</TableHead>
                              <TableHead className="text-right">HT</TableHead>
                              <TableHead className="text-right">TVA</TableHead>
                              <TableHead className="text-right">TTC</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {invoiceExportResult.invoices.map((invoice) => (
                              <TableRow key={invoice.id}>
                                <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                                <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                                <TableCell>{invoice.structure_name}</TableCell>
                                <TableCell className="text-right">{formatCurrency(invoice.amount_ht)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(invoice.amount_tva)}</TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(invoice.amount_ttc)}</TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => downloadInvoicePdf(invoice)}
                                    disabled={!invoice.pdf_base64}
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ==================== SETTINGS TAB ==================== */}
        <TabsContent value="settings">
          <div className="space-y-6">
            {/* Stripe Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Configuration Stripe
                </CardTitle>
                <CardDescription>Configurez votre clé API Stripe pour activer les paiements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm">Statut:</span>
                    {settings?.stripe_api_key_configured ? (
                      <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Configuré
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        Non configuré
                      </Badge>
                    )}
                  </div>
                  
                  <div>
                    <Label>Clé API Stripe (Secret Key)</Label>
                    <Input
                      type="password"
                      value={stripeApiKey}
                      onChange={(e) => setStripeApiKey(e.target.value)}
                      placeholder="sk_live_... ou sk_test_..."
                      className="font-mono"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Trouvez votre clé sur <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">dashboard.stripe.com/apikeys</a>
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleSaveStripeSettings} 
                    disabled={savingSettings || !stripeApiKey}
                    className="bg-[#0F5156] hover:bg-[#0d464a]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingSettings ? 'Enregistrement...' : 'Enregistrer la clé Stripe'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Anthropic API Key */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="w-5 h-5" />
                  Assistant IA (Claude)
                </CardTitle>
                <CardDescription>Configurez votre cle API Anthropic pour activer l'assistant IA RH</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm">Statut:</span>
                    {settings?.anthropic_api_key_configured ? (
                      <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Configure
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        Non configure
                      </Badge>
                    )}
                  </div>

                  <div>
                    <Label>Cle API Anthropic</Label>
                    <Input
                      type="password"
                      value={anthropicApiKey}
                      onChange={(e) => setAnthropicApiKey(e.target.value)}
                      placeholder="sk-ant-..."
                      className="font-mono"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Trouvez votre cle sur <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">console.anthropic.com</a>
                    </p>
                  </div>

                  <div>
                    <Label>Modele IA</Label>
                    <Select value={aiModel} onValueChange={setAiModel}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selectionner un modele" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5</SelectItem>
                        <SelectItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5</SelectItem>
                        <SelectItem value="claude-opus-4-6">Claude Opus 4.6</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Modele utilise pour l'assistant IA et l'extraction des notes de frais
                    </p>
                  </div>

                  <Button
                    onClick={handleSaveAnthropicSettings}
                    disabled={savingSettings}
                    className="bg-[#0F5156] hover:bg-[#0d464a]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingSettings ? 'Enregistrement...' : 'Enregistrer la configuration IA'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* SMTP Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Configuration SMTP (Emails)
                </CardTitle>
                <CardDescription>Configurez l'envoi d'emails (notifications, réinitialisation mot de passe, etc.)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm">Statut:</span>
                    {settings?.smtp_configured ? (
                      <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Configuré
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Non configuré
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Serveur SMTP</Label>
                      <Input
                        value={smtpHost}
                        onChange={(e) => setSmtpHost(e.target.value)}
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    <div>
                      <Label>Port</Label>
                      <Input
                        type="number"
                        value={smtpPort}
                        onChange={(e) => setSmtpPort(e.target.value)}
                        placeholder="587"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Utilisateur / Email</Label>
                      <Input
                        value={smtpUsername}
                        onChange={(e) => setSmtpUsername(e.target.value)}
                        placeholder="votre@email.com"
                      />
                    </div>
                    <div>
                      <Label>Mot de passe</Label>
                      <Input
                        type="password"
                        value={smtpPassword}
                        onChange={(e) => setSmtpPassword(e.target.value)}
                        placeholder="••••••••"
                      />
                      {settings?.smtp_configured && (
                        <p className="text-xs text-slate-500 mt-1">Laissez vide pour garder le mot de passe actuel</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email d'expédition</Label>
                      <Input
                        type="email"
                        value={smtpFromEmail}
                        onChange={(e) => setSmtpFromEmail(e.target.value)}
                        placeholder="noreply@nexus-sirh.fr"
                      />
                    </div>
                    <div>
                      <Label>Nom d'expédition</Label>
                      <Input
                        value={smtpFromName}
                        onChange={(e) => setSmtpFromName(e.target.value)}
                        placeholder="Nexus SIRH"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={smtpUseTls}
                      onChange={(e) => setSmtpUseTls(e.target.checked)}
                      id="smtp_tls"
                      className="w-4 h-4"
                    />
                    <Label htmlFor="smtp_tls">Utiliser TLS/SSL</Label>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={handleSaveSmtpSettings} 
                      disabled={savingSettings}
                      className="bg-[#0F5156] hover:bg-[#0d464a]"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingSettings ? 'Enregistrement...' : 'Enregistrer SMTP'}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleTestEmail}
                      disabled={testingEmail || !settings?.smtp_configured}
                    >
                      <TestTube className="w-4 h-4 mr-2" />
                      {testingEmail ? 'Envoi...' : 'Tester l\'envoi'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company Info - Société qui facture */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Informations de facturation
                </CardTitle>
                <CardDescription>
                  Informations de la societe qui apparaitront sur les factures
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Logo Upload Section */}
                  <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                    <Label className="font-medium">Logo de la societe</Label>
                    <p className="text-sm text-slate-500 mb-3">
                      Ce logo apparaitra sur vos factures. Formats acceptes: PNG, JPG, WEBP. Taille max: 2MB
                    </p>
                    <div className="flex items-center gap-4">
                      {companyLogo ? (
                        <div className="w-32 h-16 border rounded bg-white flex items-center justify-center p-2">
                          <img
                            src={companyLogo.startsWith('data:') ? companyLogo : `data:image/png;base64,${companyLogo}`}
                            alt="Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="w-32 h-16 border rounded bg-white flex items-center justify-center text-slate-400">
                          <Image className="w-8 h-8" />
                        </div>
                      )}
                      <div>
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          onClick={() => document.getElementById('logo-upload').click()}
                          disabled={uploadingLogo}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingLogo ? 'Upload...' : 'Changer le logo'}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Nom de la societe *</Label>
                      <Input
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Nexus SIRH SAS"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Adresse</Label>
                      <Input
                        value={companyAddress}
                        onChange={(e) => setCompanyAddress(e.target.value)}
                        placeholder="123 Rue de la Paie"
                      />
                    </div>
                    <div>
                      <Label>Code postal</Label>
                      <Input
                        value={companyCodePostal}
                        onChange={(e) => setCompanyCodePostal(e.target.value)}
                        placeholder="75001"
                      />
                    </div>
                    <div>
                      <Label>Ville</Label>
                      <Input
                        value={companyVille}
                        onChange={(e) => setCompanyVille(e.target.value)}
                        placeholder="Paris"
                      />
                    </div>
                    <div>
                      <Label>SIRET</Label>
                      <Input
                        value={companySiret}
                        onChange={(e) => setCompanySiret(e.target.value)}
                        placeholder="123 456 789 00012"
                      />
                    </div>
                    <div>
                      <Label>N° TVA Intracommunautaire</Label>
                      <Input
                        value={companyTva}
                        onChange={(e) => setCompanyTva(e.target.value)}
                        placeholder="FR12345678901"
                      />
                    </div>
                    <div>
                      <Label>Capital social</Label>
                      <Input
                        value={companyCapital}
                        onChange={(e) => setCompanyCapital(e.target.value)}
                        placeholder="10 000,00"
                      />
                    </div>
                    <div>
                      <Label>Ville du RCS</Label>
                      <Input
                        value={companyRcs}
                        onChange={(e) => setCompanyRcs(e.target.value)}
                        placeholder="Paris"
                      />
                    </div>
                    <div>
                      <Label>Email de contact</Label>
                      <Input
                        type="email"
                        value={companyEmail}
                        onChange={(e) => setCompanyEmail(e.target.value)}
                        placeholder="contact@nexus-sirh.fr"
                      />
                    </div>
                    <div>
                      <Label>Téléphone</Label>
                      <Input
                        value={companyPhone}
                        onChange={(e) => setCompanyPhone(e.target.value)}
                        placeholder="01 23 45 67 89"
                      />
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3">Coordonnées bancaires (pour les virements)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>IBAN</Label>
                        <Input
                          value={companyIban}
                          onChange={(e) => setCompanyIban(e.target.value.toUpperCase())}
                          placeholder="FR76 1234 5678 9012 3456 7890 123"
                        />
                      </div>
                      <div>
                        <Label>BIC</Label>
                        <Input
                          value={companyBic}
                          onChange={(e) => setCompanyBic(e.target.value.toUpperCase())}
                          placeholder="BNPAFRPP"
                        />
                      </div>
                    </div>
                  </div>

                  <Button 
                    onClick={handleSaveCompanyInfo} 
                    disabled={savingCompany}
                    className="bg-[#0F5156] hover:bg-[#0d464a]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingCompany ? 'Enregistrement...' : 'Enregistrer les informations'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* General Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Paramètres généraux
                </CardTitle>
                <CardDescription>Configuration générale de la plateforme</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Taux de TVA (%)</Label>
                      <Input
                        type="number"
                        value={tvaRate}
                        onChange={(e) => setTvaRate(e.target.value)}
                        placeholder="20"
                        step="0.1"
                        min="0"
                        max="100"
                      />
                    </div>
                    <div>
                      <Label>Durée période d'essai (jours)</Label>
                      <Input
                        type="number"
                        value={trialDays}
                        onChange={(e) => setTrialDays(e.target.value)}
                        placeholder="14"
                        min="1"
                        max="365"
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={handleSaveGeneralSettings} 
                    disabled={savingSettings}
                    className="bg-[#0F5156] hover:bg-[#0d464a]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingSettings ? 'Enregistrement...' : 'Enregistrer les paramètres'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Key className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-800">Sécurité des clés API</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Les clés API sont stockées de manière sécurisée et ne sont jamais affichées en clair après enregistrement.
                      Pour des raisons de sécurité, vous devrez entrer une nouvelle clé pour la modifier.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* ==================== DIALOGS ==================== */}
      
      {/* Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={(open) => {
        if (!open) resetPackageForm();
        setShowPackageDialog(open);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPackageCode ? 'Modifier le package' : 'Créer un nouveau package'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code (identifiant unique)*</Label>
                <Input
                  value={formCode}
                  onChange={(e) => setFormCode(e.target.value.toLowerCase().replace(/\s/g, '_'))}
                  placeholder="ex: starter"
                  disabled={!!editingPackageCode}
                />
              </div>
              <div>
                <Label>Nom*</Label>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="ex: Pack Starter"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Description du package..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prix mensuel HT (€)</Label>
                <Input
                  type="number"
                  value={formPriceMonthly}
                  onChange={(e) => setFormPriceMonthly(e.target.value)}
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <Label>Prix annuel HT (€)</Label>
                <Input
                  type="number"
                  value={formPriceAnnual}
                  onChange={(e) => setFormPriceAnnual(e.target.value)}
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div>
              <Label>Limite d'employés (vide = illimité)</Label>
              <Input
                type="number"
                value={formMaxEmployees}
                onChange={(e) => setFormMaxEmployees(e.target.value)}
                placeholder="Illimité"
                min="1"
              />
            </div>

            <div>
              <Label className="mb-2 block">Modules inclus</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {moduleEntries.map(([code, label]) => {
                  const Icon = MODULE_ICONS[code] || Settings;
                  const isSelected = formModules.includes(code);
                  return (
                    <Button
                      key={code}
                      type="button"
                      variant={isSelected ? "default" : "outline"}
                      className={`justify-start gap-2 ${isSelected ? 'bg-[#0F5156] hover:bg-[#0d464a]' : ''}`}
                      onClick={() => toggleModule(code)}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formIsActive}
                onChange={(e) => setFormIsActive(e.target.checked)}
                id="is_active"
                className="w-4 h-4"
              />
              <Label htmlFor="is_active">Package actif (visible pour les clients)</Label>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowPackageDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSavePackage} className="bg-[#0F5156] hover:bg-[#0d464a]">
              {editingPackageCode ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Dialog - Edit Only */}
      <Dialog open={showSubscriptionDialog} onOpenChange={(open) => {
        if (!open) resetSubscriptionForm();
        setShowSubscriptionDialog(open);
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier l'abonnement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700 mb-4">
              <p>Vous pouvez modifier tous les parametres de l'abonnement, y compris accorder une gratuite.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Package</Label>
                <Select value={subFormPackageCode} onValueChange={setSubFormPackageCode}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Aucun (standard)</SelectItem>
                    {packages.filter(p => p.is_active).map((pkg) => (
                      <SelectItem key={pkg.code} value={pkg.code}>
                        {pkg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Type de plan</Label>
                <Select value={subFormPlanType} onValueChange={setSubFormPlanType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensuel</SelectItem>
                    <SelectItem value="annual">Annuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Statut</Label>
                <Select value={subFormStatus} onValueChange={setSubFormStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="trial">Essai</SelectItem>
                    <SelectItem value="cancelling">En resiliation</SelectItem>
                    <SelectItem value="suspended">Suspendu</SelectItem>
                    <SelectItem value="cancelled">Annule</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Fin de periode</Label>
                <Input
                  type="date"
                  value={subFormPeriodEnd}
                  onChange={(e) => setSubFormPeriodEnd(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label>Montant HT personnalise (€)</Label>
              <Input
                type="number"
                value={subFormAmountHt}
                onChange={(e) => setSubFormAmountHt(e.target.value)}
                placeholder="Utiliser le prix du package"
                step="0.01"
                min="0"
                disabled={subFormIsFree}
              />
              <p className="text-xs text-slate-500 mt-1">Laissez vide pour utiliser le prix du package</p>
            </div>

            {/* Gratuity Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  id="isFree"
                  checked={subFormIsFree}
                  onChange={(e) => setSubFormIsFree(e.target.checked)}
                  className="w-5 h-5 rounded border-green-500 text-green-600 focus:ring-green-500"
                />
                <Label htmlFor="isFree" className="text-base font-medium text-green-700 cursor-pointer">
                  Accorder la gratuite
                </Label>
              </div>

              {subFormIsFree && (
                <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <Label>Raison de la gratuite</Label>
                    <Input
                      value={subFormFreeReason}
                      onChange={(e) => setSubFormFreeReason(e.target.value)}
                      placeholder="Ex: Partenariat, Offre speciale, Test..."
                    />
                  </div>

                  <div>
                    <Label>Gratuite jusqu'au (optionnel)</Label>
                    <Input
                      type="date"
                      value={subFormFreeUntil}
                      onChange={(e) => setSubFormFreeUntil(e.target.value)}
                    />
                    <p className="text-xs text-slate-500 mt-1">Laissez vide pour une gratuite indefinie</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="notifyClient"
                      checked={subFormNotifyClient}
                      onChange={(e) => setSubFormNotifyClient(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    <Label htmlFor="notifyClient" className="text-sm cursor-pointer">
                      Notifier le client par email
                    </Label>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscriptionDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveSubscription} className="bg-[#0F5156] hover:bg-[#0d464a]">
              {editingSubscriptionId ? 'Mettre à jour' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend / Set Trial Dialog */}
      <Dialog open={showExtendTrialDialog} onOpenChange={(open) => { setShowExtendTrialDialog(open); if (!open) setTrialMode('extend'); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la période d'essai</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p>Structure : <strong>{selectedStructure?.nom}</strong></p>
            <p>Fin d'essai actuelle : <strong>{formatDate(selectedStructure?.trial_end_date) || 'Non définie'}</strong></p>
            <div className="flex gap-2">
              <Button
                variant={trialMode === 'extend' ? 'default' : 'outline'}
                onClick={() => setTrialMode('extend')}
                className={trialMode === 'extend' ? 'bg-[#0F5156] hover:bg-[#0d464a]' : ''}
                size="sm"
              >
                Prolonger
              </Button>
              <Button
                variant={trialMode === 'set' ? 'default' : 'outline'}
                onClick={() => setTrialMode('set')}
                className={trialMode === 'set' ? 'bg-[#0F5156] hover:bg-[#0d464a]' : ''}
                size="sm"
              >
                Définir une date
              </Button>
            </div>
            {trialMode === 'extend' ? (
              <div>
                <Label>Jours à ajouter</Label>
                <Input
                  type="number"
                  value={extendDays}
                  onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                  min={1}
                  max={365}
                />
              </div>
            ) : (
              <div>
                <Label>Nouvelle date de fin</Label>
                <Input
                  type="date"
                  value={trialEndDate}
                  onChange={(e) => setTrialEndDate(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowExtendTrialDialog(false); setTrialMode('extend'); }}>
              Annuler
            </Button>
            <Button onClick={handleExtendTrial} className="bg-[#0F5156] hover:bg-[#0d464a]">
              {trialMode === 'extend' ? 'Prolonger' : 'Modifier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Package Dialog */}
      <Dialog open={showAssignPackageDialog} onOpenChange={setShowAssignPackageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assigner un package</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p>Structure : <strong>{selectedStructure?.nom}</strong></p>
            <div>
              <Label>Package</Label>
              <Select value={assignPackageCode} onValueChange={setAssignPackageCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un package" />
                </SelectTrigger>
                <SelectContent>
                  {packages.filter(p => p.is_active).map((pkg) => (
                    <SelectItem key={pkg.code} value={pkg.code}>
                      {pkg.name} - {formatCurrency(pkg.price_monthly_ttc)}/mois
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignPackageDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleAssignPackage} 
              className="bg-[#0F5156] hover:bg-[#0d464a]"
              disabled={!assignPackageCode}
            >
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Structure Detail Dialog */}
      <Dialog open={showStructureDetailDialog} onOpenChange={setShowStructureDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails de la structure</DialogTitle>
          </DialogHeader>
          {selectedStructure && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Nom</Label>
                  <p className="font-medium">{selectedStructure.nom}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Statut</Label>
                  <p>{getStatusBadge(selectedStructure.status)}</p>
                </div>
                <div>
                  <Label className="text-slate-500">SIRET</Label>
                  <p className="font-mono">{selectedStructure.siret}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Email</Label>
                  <p>{selectedStructure.email}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Téléphone</Label>
                  <p>{selectedStructure.telephone || '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Package</Label>
                  <p>{selectedStructure.package_code || 'Standard'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-500">Adresse</Label>
                  <p>{selectedStructure.adresse}, {selectedStructure.code_postal} {selectedStructure.ville}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Date d'inscription</Label>
                  <p>{formatDate(selectedStructure.created_at)}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Fin période d'essai</Label>
                  <p>{formatDate(selectedStructure.trial_end_date) || '-'}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStructureDetailDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
